import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useInventory } from '../contexts/InventoryContext';
import { useToast } from '../contexts/ToastContext';
import { redirectToCheckout } from '../services/stripeService';
import { formatStockMessage, canIncreaseQuantity, findCartStockIssues } from '../utils/stockUtils';
import { formatPhone, isValidPhone } from '../utils/phoneUtils';
import { fetchAddressByCep } from '../utils/viaCep';
import { LOCAL_DELIVERY_HINT } from '../constants/localDelivery';
import './CheckoutPage.css';

const base = process.env.REACT_APP_API_URL || '';

const formatFretePrice = (price, formatPrice) =>
  price <= 0 ? 'Grátis' : formatPrice(price);

const formatFretePrazo = (opt) =>
  opt.localDelivery ? 'Entrega combinada' : `${opt.deliveryTime} dias úteis`;

const formatCep = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
};

const CheckoutForm = ({ getStockForSize }) => {
  const { cartItems, getTotalPrice, formatPrice, getTotalItems } = useCart();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [cep, setCep] = useState('');
  const [street, setStreet] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [number, setNumber] = useState('');
  const [complement, setComplement] = useState('');
  const [phone, setPhone] = useState('');

  const [freteOptions, setFreteOptions] = useState([]);
  const [selectedFrete, setSelectedFrete] = useState(null);
  const [freteLoading, setFreteLoading] = useState(false);
  const [freteError, setFreteError] = useState('');
  const [freteCalculated, setFreteCalculated] = useState(false);

  const resetAddressAndFrete = () => {
    setFreteCalculated(false);
    setFreteOptions([]);
    setSelectedFrete(null);
    setFreteError('');
    setStreet('');
    setNeighborhood('');
    setCity('');
    setState('');
    setNumber('');
    setComplement('');
  };

  const handleCepChange = (e) => {
    setCep(formatCep(e.target.value));
    resetAddressAndFrete();
  };

  const calcularFrete = async (e) => {
    e.preventDefault();
    const rawCep = cep.replace(/\D/g, '');
    if (rawCep.length !== 8) {
      setFreteError('Digite um CEP válido com 8 dígitos.');
      return;
    }

    setFreteLoading(true);
    setFreteError('');

    try {
      const address = await fetchAddressByCep(rawCep);
      setStreet(address.street);
      setNeighborhood(address.neighborhood);
      setCity(address.city);
      setState(address.state);

      const res = await fetch(
        `${base}/api/calcular-frete?cep=${rawCep}&quantidade=${getTotalItems()}`
      );
      const data = await res.json();
      if (!res.ok) {
        setFreteError(data.error || 'Erro ao calcular frete.');
        return;
      }
      if (!Array.isArray(data) || data.length === 0) {
        setFreteError('Nenhuma opção de frete disponível para este CEP.');
        return;
      }

      setFreteOptions(data);
      setSelectedFrete(data[0]);
      setFreteCalculated(true);
    } catch (err) {
      setFreteError(err.message || 'Não foi possível calcular o frete. Tente novamente.');
      setStreet('');
      setNeighborhood('');
      setCity('');
      setState('');
    } finally {
      setFreteLoading(false);
    }
  };

  const subtotal = getTotalPrice();
  const shippingCost = selectedFrete?.price || 0;
  const total = subtotal + shippingCost;

  const handleCheckout = async (e) => {
    e.preventDefault();
    setError('');

    const stockIssues = findCartStockIssues(cartItems, getStockForSize);
    if (stockIssues.length > 0) {
      const { item, stock } = stockIssues[0];
      setError(
        `${item.name} (tam. ${item.size}): ${formatStockMessage(stock, item.size)} Ajuste a quantidade antes de pagar.`
      );
      return;
    }

    if (!freteCalculated || !selectedFrete) {
      setError('Calcule o frete antes de continuar.');
      return;
    }

    if (!street.trim()) {
      setError('Informe a rua do endereço.');
      return;
    }

    if (!number.trim()) {
      setError('Informe o número do endereço.');
      return;
    }

    if (!isValidPhone(phone)) {
      setError('Informe um telefone válido com DDD.');
      return;
    }

    setLoading(true);
    try {
      const shippingData = {
        ...selectedFrete,
        cep,
        street: street.trim(),
        neighborhood: neighborhood.trim(),
        city: city.trim(),
        state: state.trim(),
        number: number.trim(),
        complement: complement.trim(),
        phone: phone.trim(),
      };
      await redirectToCheckout(cartItems, currentUser?.email || '', shippingData);
    } catch (err) {
      setError(err.message || 'Erro ao processar pagamento.');
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleCheckout} className="checkout-form">
      <div className="checkout-section">
        <h3 className="checkout-section-title">Entrega</h3>
        <div className="checkout-cep-row">
          <input
            type="text"
            placeholder="CEP (ex: 01310-100)"
            value={cep}
            onChange={handleCepChange}
            className="checkout-cep-input"
            maxLength={9}
          />
          <button
            type="button"
            onClick={calcularFrete}
            disabled={freteLoading}
            className="btn btn-outline checkout-cep-btn"
          >
            <span>{freteLoading ? 'Buscando...' : 'Calcular frete'}</span>
          </button>
        </div>
        {freteError && <p className="checkout-error">{freteError}</p>}

        {freteCalculated && (
          <>
            <div className="checkout-address-fields">
              <div className="checkout-address-field checkout-address-field--full">
                <label htmlFor="checkout-street" className="checkout-address-label">Rua *</label>
                <input
                  id="checkout-street"
                  type="text"
                  placeholder="Nome da rua"
                  value={street}
                  onChange={(e) => setStreet(e.target.value)}
                  className="checkout-address-input"
                />
              </div>

              <div className="checkout-address-row">
                <div className="checkout-address-field">
                  <label htmlFor="checkout-number" className="checkout-address-label">Número *</label>
                  <input
                    id="checkout-number"
                    type="text"
                    placeholder="Ex: 123"
                    value={number}
                    onChange={(e) => setNumber(e.target.value)}
                    className="checkout-address-input"
                  />
                </div>
                <div className="checkout-address-field checkout-address-field--wide">
                  <label htmlFor="checkout-complement" className="checkout-address-label">Complemento</label>
                  <input
                    id="checkout-complement"
                    type="text"
                    placeholder="Apto, bloco, referência..."
                    value={complement}
                    onChange={(e) => setComplement(e.target.value)}
                    className="checkout-address-input"
                  />
                </div>
              </div>

              <div className="checkout-address-field checkout-address-field--full">
                <label htmlFor="checkout-neighborhood" className="checkout-address-label">Bairro</label>
                <input
                  id="checkout-neighborhood"
                  type="text"
                  placeholder="Bairro"
                  value={neighborhood}
                  onChange={(e) => setNeighborhood(e.target.value)}
                  className="checkout-address-input"
                />
              </div>

              {(city || state) && (
                <p className="checkout-city-state">
                  {city}{city && state ? ' — ' : ''}{state}
                </p>
              )}

              <div className="checkout-address-field checkout-address-field--full">
                <label htmlFor="checkout-phone" className="checkout-address-label">Telefone *</label>
                <input
                  id="checkout-phone"
                  type="tel"
                  placeholder="(11) 99999-9999"
                  value={phone}
                  onChange={(e) => setPhone(formatPhone(e.target.value))}
                  className="checkout-address-input"
                  autoComplete="tel"
                />
              </div>
            </div>

            <div className="checkout-frete-options">
              {freteOptions.map((opt) => (
                <label
                  key={opt.id}
                  className={`checkout-frete-option ${selectedFrete?.id === opt.id ? 'selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="frete"
                    value={opt.id}
                    checked={selectedFrete?.id === opt.id}
                    onChange={() => setSelectedFrete(opt)}
                  />
                  <span className="frete-label">{opt.label}</span>
                  <span className="frete-prazo">{formatFretePrazo(opt)}</span>
                  <span className={`frete-price ${opt.price <= 0 ? 'frete-price--free' : ''}`}>
                    {formatFretePrice(opt.price, formatPrice)}
                  </span>
                </label>
              ))}
            </div>

            {selectedFrete?.localDelivery && (
              <p className="checkout-local-hint">{LOCAL_DELIVERY_HINT}</p>
            )}
          </>
        )}
      </div>

      <div className="checkout-section">
        <h3 className="checkout-section-title">Pagamento</h3>
        <p className="checkout-description">
          Você será redirecionado para o Stripe para pagar com <strong>cartão</strong>.
          O endereço já foi informado acima — lá é só o pagamento.
        </p>
        {error && <div className="checkout-error">{error}</div>}
      </div>

      <div className="checkout-summary">
        <div className="checkout-summary-row">
          <span>Subtotal:</span>
          <span>{formatPrice(subtotal)}</span>
        </div>
        {shippingCost > 0 && (
          <div className="checkout-summary-row">
            <span>Frete ({selectedFrete?.label}):</span>
            <span>{formatPrice(shippingCost)}</span>
          </div>
        )}
        {freteCalculated && shippingCost <= 0 && selectedFrete && (
          <div className="checkout-summary-row">
            <span>Frete ({selectedFrete.label}):</span>
            <span className="checkout-frete-free">Grátis</span>
          </div>
        )}
        {!freteCalculated && (
          <div className="checkout-summary-row checkout-frete-hint">
            <span>Frete:</span>
            <span>Calcule acima</span>
          </div>
        )}
        <div className="checkout-summary-row checkout-total">
          <span>Total:</span>
          <span>{formatPrice(total)}</span>
        </div>
        {!freteCalculated && (
          <p className="checkout-frete-required">Informe o CEP e calcule o frete antes de continuar.</p>
        )}
        <button
          type="submit"
          disabled={loading || cartItems.length === 0 || !freteCalculated}
          className="btn btn-filled checkout-submit"
        >
          <span>{loading ? 'Redirecionando...' : `Pagar ${formatPrice(total)}`}</span>
        </button>
      </div>
    </form>
  );
};

const CheckoutPage = () => {
  const { cartItems, cartItemKey, updateQuantity, removeFromCart } = useCart();
  const { currentUser } = useAuth();
  const { loadInventory, getStockForSize } = useInventory();
  const { error: toastError, info: toastInfo } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser) {
      navigate('/');
      return;
    }
    if (!currentUser.emailVerified) {
      toastInfo('Confirme seu email antes de finalizar a compra. Verifique também a pasta de spam.');
      navigate('/');
      return;
    }
    if (cartItems.length === 0) navigate('/');
  }, [currentUser, cartItems.length, navigate, toastInfo]);

  useEffect(() => {
    cartItems.forEach((item) => {
      if (item.id) loadInventory(item.id);
    });
  }, [cartItems, loadInventory]);

  const handleIncrease = (item, key) => {
    if (!item.id || !item.size) {
      updateQuantity(key, item.quantity + 1);
      return;
    }
    const stock = getStockForSize(item.id, item.size);
    if (!canIncreaseQuantity(stock, item.quantity)) {
      toastError(formatStockMessage(stock, item.size));
      return;
    }
    updateQuantity(key, item.quantity + 1);
  };

  if (!currentUser || cartItems.length === 0) return null;

  return (
    <div className="checkout-page">
      <div className="checkout-container">
        <h1 className="checkout-title">Finalizar Compra</h1>
        <div className="checkout-content">
          <div className="checkout-items">
            <h2 className="checkout-items-title">Seu Pedido</h2>
            {cartItems.map((item) => {
              const key = item.cartKey || cartItemKey(item);
              const stock = item.id && item.size ? getStockForSize(item.id, item.size) : Infinity;
              const atMax = item.id && item.size && item.quantity >= stock;

              return (
                <div key={key} className="checkout-item">
                  <div className="checkout-item-image">
                    {item.thumbnail ? (
                      <img src={item.thumbnail} alt={item.name} loading="lazy" />
                    ) : (
                      <div className="checkout-item-placeholder">HAKA</div>
                    )}
                  </div>
                  <div className="checkout-item-info">
                    <h3>{item.name}</h3>
                    {item.size && <p>Tamanho: {item.size}</p>}
                    <div className="checkout-item-quantity">
                      <button
                        type="button"
                        onClick={() => updateQuantity(key, item.quantity - 1)}
                        className="quantity-btn"
                        disabled={item.quantity <= 1}
                      >
                        -
                      </button>
                      <span className="quantity-value">{item.quantity}</span>
                      <button
                        type="button"
                        onClick={() => handleIncrease(item, key)}
                        className="quantity-btn"
                        disabled={atMax}
                        title={atMax ? formatStockMessage(stock, item.size) : undefined}
                      >
                        +
                      </button>
                      <button
                        type="button"
                        onClick={() => removeFromCart(key)}
                        className="remove-btn"
                      >
                        Remover
                      </button>
                    </div>
                    {atMax && (
                      <p className="checkout-stock-hint">{formatStockMessage(stock, item.size)}</p>
                    )}
                    <p>{item.price}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="checkout-payment">
            <CheckoutForm getStockForSize={getStockForSize} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
